import type { ReactNode } from "react"
import { useCallback, useRef, useState } from "react"

import { reconfirmTwoFactor } from "../api/mfa.api"
import { type ApiProblem, isApiProblem } from "../api/problem"
import { JournalDialog } from "../journal/appointment-dialogs"
import { Button, Checkbox, TextField } from "../ui"
import styles from "./fresh-two-factor.module.css"

/**
 * ПОВТОРНОЕ ПОДТВЕРЖДЕНИЕ ВТОРЫМ ФАКТОРОМ — окно посреди действия (ТЗ 01, окно
 * подтверждения перед операциями с массивом ПД).
 *
 * Сервер сам решает, нужна ли свежесть: отказ с кодом «подтвердите» приходит в ответ
 * на само действие (two_factor_stale из входа, mfa_reconfirm_required из кабинета).
 * Экран не угадывает это заранее — отметка живёт в токене, и любое предсказание
 * разошлось бы с сервером на секунды до истечения. Поэтому схема простая: выполнить,
 * получить «подтвердите», спросить код, выполнить ещё раз — один раз, без карусели.
 */
export function isTwoFactorStale(problem: ApiProblem): boolean {
  return problem.code === "two_factor_stale" || problem.code === "mfa_reconfirm_required"
}

/** Текст отказа при вводе кода — по коду проблемы, а не по разбору русского текста. */
export function reconfirmProblemText(problem: ApiProblem): string {
  if (problem.code === "mfa_not_enrolled") {
    return "Второй фактор у вас не включён. Включите его в профиле («Безопасность»), затем повторите действие."
  }
  if (problem.code === "mfa_invalid") {
    return "Код не подошёл. Проверьте время на телефоне и введите новый код из приложения."
  }
  if (problem.code === "offline" || problem.code === "timeout") {
    return "Нет связи: код не проверен. Повторите, когда связь вернётся."
  }
  return problem.detail ?? problem.title
}

type Pending = {
  readonly purpose: string
  readonly retry: () => Promise<unknown>
  readonly resolve: (value: unknown) => void
  readonly reject: (error: unknown) => void
}

/**
 * Обёртка действия: `run(() => запрос, "вы выгружаете базу клиентов")` выполняет его, а при отказе «подтвердите» показывает
 * окно (`dialog` — его нужно отрисовать на экране) и после верного кода повторяет запрос.
 * Отказ человека от подтверждения — исходная ошибка: экран покажет её как обычный отказ.
 *
 * ЧТО ПОДТВЕРЖДАЕТСЯ — ОБЯЗАТЕЛЬНО (ТЗ 01, §595): «Подтвердите, что это вы: вы открываете
 * доступ к телефонам клиентов». То же окно без этой строки читается как случайный запрос
 * пароля — а подозрение здесь как раз уместно, и человек должен понять, на что соглашается.
 */
export function useFreshTwoFactor(): {
  readonly run: <T>(action: () => Promise<T>, purpose: string) => Promise<T>
  readonly dialog: ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)
  const original = useRef<unknown>(null)

  const run = useCallback(<T,>(action: () => Promise<T>, purpose: string): Promise<T> => {
    return action().catch((error: unknown) => {
      if (!isApiProblem(error) || !isTwoFactorStale(error)) throw error
      original.current = error
      return new Promise<T>((resolve, reject) => {
        setPending({ purpose, retry: action, resolve: resolve as (value: unknown) => void, reject })
      })
    })
  }, [])

  const dialog =
    pending === null ? null : (
      <ReconfirmDialog
        purpose={pending.purpose}
        onConfirmed={() => {
          const current = pending
          setPending(null)
          current.retry().then(current.resolve, current.reject)
        }}
        onCancel={() => {
          const current = pending
          setPending(null)
          current.reject(original.current)
        }}
      />
    )

  return { run, dialog }
}

function ReconfirmDialog(props: {
  readonly purpose: string
  readonly onConfirmed: () => void
  readonly onCancel: () => void
}): ReactNode {
  const [code, setCode] = useState("")
  const [recovery, setRecovery] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    if (code.trim().length < 6) {
      setProblem(recovery ? "Введите резервный код целиком" : "Введите шесть цифр из приложения")
      return
    }
    setBusy(true)
    setProblem(null)
    try {
      await reconfirmTwoFactor(code.trim(), recovery ? "recovery" : "totp")
      props.onConfirmed()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(reconfirmProblemText(error))
      setCode("")
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Подтвердите, что это вы" onClose={props.onCancel}>
      <form
        className={styles.body}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        <p className={styles.purpose}>Подтвердите, что это вы: {props.purpose}.</p>
        <p className={styles.text}>
          Введите код из приложения-аутентификатора. Подтверждения хватит на 15 минут — повторно спрашивать не будем.
        </p>
        <TextField
          label={recovery ? "Резервный код" : "Код из приложения"}
          value={code}
          inputMode={recovery ? "text" : "numeric"}
          autoComplete="one-time-code"
          autoFocus
          maxLength={64}
          error={problem ?? undefined}
          onChange={(event) => {
            setCode(event.target.value)
            setProblem(null)
          }}
        />
        <Checkbox checked={recovery} onChange={setRecovery}>
          Телефона с приложением нет под рукой — ввести резервный код
        </Checkbox>
        <div className={styles.buttons}>
          <Button onClick={props.onCancel}>Отмена</Button>
          <Button variant="primary" type="submit" loading={busy}>
            Подтвердить
          </Button>
        </div>
      </form>
    </JournalDialog>
  )
}
